import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import type {
  IHttpClient,
  HttpRequestConfig,
  HttpResponse,
  RequestInterceptor,
  ResponseInterceptor,
  ProgressEvent,
  HttpError,
} from '../types/httpClient.interface';

/**
 * Axios adapter that implements the framework-agnostic IHttpClient interface
 * This is the only place where Axios types should be referenced
 */
export class AxiosAdapter implements IHttpClient {
  private axiosInstance: AxiosInstance;

  constructor(baseURL: string, defaultHeaders?: Record<string, string>) {
    this.axiosInstance = axios.create({
      baseURL,
      headers: defaultHeaders || {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
  }

  /**
   * Convert our framework-agnostic config to Axios config
   */
  private toAxiosConfig(config?: HttpRequestConfig): AxiosRequestConfig {
    if (!config) return {};

    const axiosConfig: AxiosRequestConfig = {
      headers: config.headers,
      params: config.params,
      timeout: config.timeout,
      responseType: config.responseType,
    };

    // Map AbortSignal to Axios cancel token
    if (config.signal) {
      axiosConfig.signal = config.signal;
    }

    // Map progress events
    if (config.onUploadProgress) {
      axiosConfig.onUploadProgress = (axiosEvent) => {
        const progressEvent: ProgressEvent = {
          loaded: axiosEvent.loaded,
          total: axiosEvent.total,
          progress: axiosEvent.total ? axiosEvent.loaded / axiosEvent.total : undefined,
        };
        config.onUploadProgress!(progressEvent);
      };
    }

    if (config.onDownloadProgress) {
      axiosConfig.onDownloadProgress = (axiosEvent) => {
        const progressEvent: ProgressEvent = {
          loaded: axiosEvent.loaded,
          total: axiosEvent.total,
          progress: axiosEvent.total ? axiosEvent.loaded / axiosEvent.total : undefined,
        };
        config.onDownloadProgress!(progressEvent);
      };
    }

    return axiosConfig;
  }

  /**
   * Convert Axios response to our framework-agnostic response
   */
  private toHttpResponse<T>(axiosResponse: AxiosResponse<T>): HttpResponse<T> {
    return {
      data: axiosResponse.data,
      status: axiosResponse.status,
      statusText: axiosResponse.statusText,
      headers: axiosResponse.headers as Record<string, string>,
    };
  }

  /**
   * Convert Axios error to our framework-agnostic error
   */
  private toHttpError(error: unknown): HttpError {
    if (
      typeof error === 'object' &&
      error !== null &&
      'isHttpError' in error &&
      (error as HttpError).isHttpError === true
    ) {
      return error as HttpError;
    }

    if (axios.isAxiosError(error)) {
      const httpError = new Error(error.message) as HttpError;
      httpError.isHttpError = true;
      
      if (error.response) {
        httpError.response = {
          data: error.response.data,
          status: error.response.status,
          statusText: error.response.statusText,
          headers: error.response.headers as Record<string, string>,
        };
      }
      
      if (error.config) {
        httpError.config = {
          headers: error.config.headers as Record<string, string>,
        };
      }

      return httpError;
    }
    
    // If it's not an Axios error, wrap it
    const httpError = new Error(error instanceof Error ? error.message : 'Unknown error') as HttpError;
    httpError.isHttpError = true;
    return httpError;
  }

  async get<T = unknown>(url: string, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    try {
      const response = await this.axiosInstance.get<T>(url, this.toAxiosConfig(config));
      return this.toHttpResponse(response);
    } catch (error) {
      throw this.toHttpError(error);
    }
  }

  async post<T = unknown>(url: string, data?: unknown, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    try {
      const response = await this.axiosInstance.post<T>(url, data, this.toAxiosConfig(config));
      return this.toHttpResponse(response);
    } catch (error) {
      throw this.toHttpError(error);
    }
  }

  async put<T = unknown>(url: string, data?: unknown, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    try {
      const response = await this.axiosInstance.put<T>(url, data, this.toAxiosConfig(config));
      return this.toHttpResponse(response);
    } catch (error) {
      throw this.toHttpError(error);
    }
  }

  async patch<T = unknown>(url: string, data?: unknown, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    try {
      const response = await this.axiosInstance.patch<T>(url, data, this.toAxiosConfig(config));
      return this.toHttpResponse(response);
    } catch (error) {
      throw this.toHttpError(error);
    }
  }

  async delete<T = unknown>(url: string, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    try {
      const response = await this.axiosInstance.delete<T>(url, this.toAxiosConfig(config));
      return this.toHttpResponse(response);
    } catch (error) {
      throw this.toHttpError(error);
    }
  }

  addRequestInterceptor(interceptor: RequestInterceptor): number {
    return this.axiosInstance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const httpConfig = await interceptor.onFulfilled({
          headers: config.headers as Record<string, string>,
          params: config.params,
          timeout: config.timeout,
        } as HttpRequestConfig);
        return { ...config, ...httpConfig } as InternalAxiosRequestConfig;
      },
      interceptor.onRejected,
    );
  }

  addResponseInterceptor(interceptor: ResponseInterceptor): number {
    return this.axiosInstance.interceptors.response.use(
      async (response: AxiosResponse) => {
        const httpResponse = await interceptor.onFulfilled(this.toHttpResponse(response));
        // Return axios response with potentially modified data
        return { ...response, data: httpResponse.data };
      },
      async (error: unknown) => {
        if (interceptor.onRejected) {
          return interceptor.onRejected(this.toHttpError(error));
        }
        throw this.toHttpError(error);
      },
    );
  }

  removeRequestInterceptor(id: number): void {
    this.axiosInstance.interceptors.request.eject(id);
  }

  removeResponseInterceptor(id: number): void {
    this.axiosInstance.interceptors.response.eject(id);
  }

  createAbortController(): AbortController {
    return new AbortController();
  }

  /**
   * For advanced use cases, expose the underlying Axios instance
   * Use with caution as this breaks abstraction
   */
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}
